<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import {
    CandlestickSeries,
    type IChartApi,
    LineSeries,
    type ISeriesApi,
    type ISeriesMarkersPluginApi,
    type Time,
    type UTCTimestamp,
    createChart,
    createSeriesMarkers,
  } from 'lightweight-charts';
  import type { RenkoBlockDoc, SessionTradeDoc } from '$lib/api/charts';
  import { themeStore } from '$lib/stores/theme.svelte';
  import {
    type ChartTheme,
    etWallClockToUtcSeconds,
    getChartTheme,
  } from '$lib/utils/chart-helpers';

  // ============================================================
  // <RenkoChart>  -  Fase 5 v2.
  //
  // Diferencias vs primera version (Fase 5 v1):
  //   - Eje X DISCRETO por block index (no UTC seconds). v1 igualaba a v1
  //     original (Plotly) en este aspecto: lo importante en un renko es la
  //     secuencia de bloques, no el espacio temporal real entre ellos.
  //   - Connection line entry->exit de los trades (verde wins / rojo losses)
  //     como v1 Plotly original.
  //   - Markers mas legibles (text en cada uno con LONG/SHORT y +/-$pnl).
  //
  // Sync zoom con CandlestickChart: ya no es uno-a-uno por tiempo. El parent
  // recibe la IChartApi via onReady pero NO sincroniza time scales — los
  // dominios son distintos (UTC seconds vs block index). Decision: ChartAnalysis
  // detecta `isRenko: true` y no engancha el sync para este chart.
  // ============================================================

  interface Props {
    blocks: RenkoBlockDoc[];
    trades?: SessionTradeDoc[];
    height?: number;
    onReady?: (chart: IChartApi) => void;
  }

  let { blocks, trades = [], height = 520, onReady }: Props = $props();

  let container: HTMLDivElement;
  let chart: IChartApi | null = null;
  let blockSeries: ISeriesApi<'Candlestick'> | null = null;
  let winLineSeries: ISeriesApi<'Line'> | null = null;
  let lossLineSeries: ISeriesApi<'Line'> | null = null;
  let markersPlugin: ISeriesMarkersPluginApi<Time> | null = null;

  let theme = $state<ChartTheme>({
    background: '#1a1a1a',
    foreground: '#f5f1e8',
    border: '#404040',
    grid: '#404040',
    success: '#4ade80',
    destructive: '#f87171',
    primary: '#D87807',
    gold: '#d4af37',
    muted: '#b0b0b0',
  });

  // ----- Block -> CandlestickData con eje X DISCRETO por block.index -----
  //
  // Lightweight Charts solo acepta UTCTimestamp como Time en CandlestickSeries.
  // Truco: usamos un epoch fake de 1 segundo por block (anchor en 2000-01-01
  // 00:00:00 UTC) para que el axis quede como indice secuencial sin saltos.
  // El tickMarkFormatter sobreescribe el axis label con "#N" para que parezca
  // un eje discreto.
  const ANCHOR_UTC_SECONDS = 946_684_800; // 2000-01-01T00:00:00Z

  type BlockOhlc = {
    time: UTCTimestamp;
    open: number;
    high: number;
    low: number;
    close: number;
    index: number;
  };

  // Map block.index → posicion lineal 0..N-1 para tener un eje continuo aunque
  // haya gaps en block.index del backend. blockSorted preserva el orden original
  // por time real (la secuencia historica).
  let blocksSorted = $derived.by(() => {
    return [...blocks]
      .filter((b) => Number.isFinite(b.open) && Number.isFinite(b.close))
      .sort((a, b) => {
        const ta = etWallClockToUtcSeconds(a.time);
        const tb = etWallClockToUtcSeconds(b.time);
        if (ta !== tb) return ta - tb;
        return a.index - b.index;
      });
  });

  // index del block.index original → posicion 0..N-1 en el axis discreto.
  let indexToPosition = $derived.by(() => {
    const map = new Map<number, number>();
    blocksSorted.forEach((b, i) => map.set(b.index, i));
    return map;
  });

  // exit/entry de un trade → posicion en el eje discreto basada en time real.
  // Encuentra el block cuya time real sea >= a la del trade.
  function tradeTimeToPosition(tradeIso: string | null): number | null {
    if (!tradeIso) return null;
    const tMs = Date.parse(tradeIso);
    if (!Number.isFinite(tMs)) return null;
    const tSec = Math.floor(tMs / 1000);
    // Busco el ultimo block cuyo time real <= tSec. Esto coloca el marker
    // sobre el block donde "estaba" el precio al momento del trade.
    let pos = -1;
    for (let i = 0; i < blocksSorted.length; i++) {
      const b = blocksSorted[i];
      if (!b) continue;
      const bSec = etWallClockToUtcSeconds(b.time);
      if (bSec <= tSec) pos = i;
      else break;
    }
    return pos >= 0 ? pos : null;
  }

  function getBlockOhlc(): BlockOhlc[] {
    return blocksSorted.map((b, i) => ({
      time: (ANCHOR_UTC_SECONDS + i) as UTCTimestamp,
      open: b.open,
      high: Math.max(b.open, b.close),
      low: Math.min(b.open, b.close),
      close: b.close,
      index: b.index,
    }));
  }

  // ----- Markers: usar time = ANCHOR + position -----
  // #N global por orden de entryTime ASC (mismo patron que CandlestickChart):
  // permite matchear visualmente entry↔exit del mismo trade.
  function buildMarkers() {
    type Marker = {
      time: UTCTimestamp;
      position: 'aboveBar' | 'belowBar';
      color: string;
      shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
      text: string;
    };
    const numbered = trades
      .map((t) => ({ trade: t, entryMs: t.entryTime ? Date.parse(t.entryTime) : NaN }))
      .filter((x): x is { trade: typeof x.trade; entryMs: number } => Number.isFinite(x.entryMs))
      .sort((a, b) => a.entryMs - b.entryMs)
      .map((x, i) => ({ trade: x.trade, num: i + 1 }));

    const out: Marker[] = [];
    for (const { trade: t, num } of numbered) {
      const entryPos = tradeTimeToPosition(t.entryTime);
      if (entryPos != null && t.side) {
        out.push({
          time: (ANCHOR_UTC_SECONDS + entryPos) as UTCTimestamp,
          position: t.side === 'long' ? 'belowBar' : 'aboveBar',
          color: t.side === 'long' ? theme.success : theme.destructive,
          shape: t.side === 'long' ? 'arrowUp' : 'arrowDown',
          text: `${t.side.toUpperCase()} #${num}`,
        });
      }
      if (t.status === 'closed') {
        const exitPos = tradeTimeToPosition(t.exitTime);
        if (exitPos != null) {
          const win = t.pnl >= 0;
          const pnlText = win ? `+$${t.pnl.toFixed(0)}` : `-$${Math.abs(t.pnl).toFixed(0)}`;
          // PX5: prefijo ⚡ en exits intra-candle (closeReason polling-*).
          const isPollingExit =
            typeof t.closeReason === 'string' && t.closeReason.startsWith('polling-');
          const prefix = isPollingExit ? '⚡ ' : '';
          out.push({
            time: (ANCHOR_UTC_SECONDS + exitPos) as UTCTimestamp,
            position: win ? 'aboveBar' : 'belowBar',
            color: win ? theme.success : theme.destructive,
            shape: win ? 'circle' : 'square',
            text: `${prefix}${pnlText} #${num}`,
          });
        }
      }
    }
    // Lightweight requiere markers ordenados por time ASC; si hay duplicados
    // los preserva pero el orden importa para el render.
    out.sort((a, b) => (a.time as number) - (b.time as number));
    return out;
  }

  // ----- Connection lines entry->exit (wins/losses separados con gaps) -----
  function buildConnectionLines() {
    type Point = { time: UTCTimestamp; value: number } | { time: UTCTimestamp };

    const segments = trades
      .filter((t) => t.status === 'closed' && t.entryPrice != null && t.exitPrice != null)
      .map((t) => {
        const entryPos = tradeTimeToPosition(t.entryTime);
        const exitPos = tradeTimeToPosition(t.exitTime);
        return {
          entryPos,
          exitPos,
          entryPrice: t.entryPrice as number,
          exitPrice: t.exitPrice as number,
          pnl: t.pnl,
        };
      })
      .filter(
        (s): s is { entryPos: number; exitPos: number; entryPrice: number; exitPrice: number; pnl: number } =>
          s.entryPos != null && s.exitPos != null && s.exitPos >= s.entryPos,
      )
      .sort((a, b) => a.entryPos - b.entryPos);

    const wins: Point[] = [];
    const losses: Point[] = [];
    let lastWinPos = -Infinity;
    let lastLossPos = -Infinity;

    for (const s of segments) {
      const isWin = s.pnl >= 0;
      const lastPos = isWin ? lastWinPos : lastLossPos;
      let entryPos = s.entryPos;
      let exitPos = s.exitPos > s.entryPos ? s.exitPos : s.entryPos + 1;
      // Misma logica que el candle anterior: si dos trades se solapan,
      // shifteamos el entryPos para preservar monotonia estricta.
      if (entryPos <= lastPos) {
        entryPos = lastPos + 1;
        if (exitPos <= entryPos) exitPos = entryPos + 1;
      }
      const target = isWin ? wins : losses;
      target.push({ time: (ANCHOR_UTC_SECONDS + entryPos) as UTCTimestamp, value: s.entryPrice });
      target.push({ time: (ANCHOR_UTC_SECONDS + exitPos) as UTCTimestamp, value: s.exitPrice });
      const gapPos = exitPos + 1;
      target.push({ time: (ANCHOR_UTC_SECONDS + gapPos) as UTCTimestamp });
      if (isWin) lastWinPos = gapPos;
      else lastLossPos = gapPos;
    }

    return { wins, losses };
  }

  // ----- tickMarkFormatter discreto: convertir time-anchor → "#N" -----
  function discreteTickFormatter(time: number): string {
    const pos = time - ANCHOR_UTC_SECONDS;
    if (pos < 0 || !Number.isInteger(pos)) return '';
    const block = blocksSorted[pos];
    if (!block) return `#${pos}`;
    return `#${block.index}`;
  }

  // ----- Crosshair tooltip -----
  let tooltipVisible = $state(false);
  let tooltipContent = $state<{
    time: string;
    direction: 'up' | 'down';
    index: number;
    open: number;
    close: number;
    boxSize: number;
    atr: number;
  } | null>(null);
  let tooltipX = $state(0);
  let tooltipY = $state(0);

  function setupCrosshair() {
    if (!chart || !blockSeries) return;
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || param.point.x < 0 || param.point.y < 0 || !param.time) {
        tooltipVisible = false;
        return;
      }
      const pos = (param.time as number) - ANCHOR_UTC_SECONDS;
      const block = blocksSorted[pos];
      if (!block) {
        tooltipVisible = false;
        return;
      }
      // El time real del block (en ET wall-clock) lo mostramos como referencia.
      const tSec = etWallClockToUtcSeconds(block.time);
      const date = new Date(tSec * 1000);
      const etFmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
      tooltipContent = {
        time: etFmt.format(date),
        direction: block.direction,
        index: block.index,
        open: block.open,
        close: block.close,
        boxSize: block.boxSize,
        atr: block.candleAtr,
      };
      tooltipVisible = true;
      tooltipX = param.point.x + 12;
      tooltipY = param.point.y + 12;
    });
  }

  onMount(() => {
    theme = getChartTheme();
    const initialWidth = container.clientWidth > 0 ? container.clientWidth : 600;
    chart = createChart(container, {
      width: initialWidth,
      height,
      layout: {
        background: { color: theme.background },
        textColor: theme.foreground,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      grid: {
        vertLines: { color: theme.grid },
        horzLines: { color: theme.grid },
      },
      rightPriceScale: { borderColor: theme.border },
      timeScale: {
        borderColor: theme.border,
        timeVisible: false,
        secondsVisible: false,
        // Eje X discreto: mostrar block index real, no tiempo.
        tickMarkFormatter: (time: Time) => discreteTickFormatter(time as number),
      },
      crosshair: { mode: 1 },
      // Mismos controles que CandlestickChart: scroll-wheel zoom, click+drag pan,
      // eje no zoomea al arrastrar.
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        mouseWheel: true,
        pinch: true,
        axisPressedMouseMove: false,
        axisDoubleClickReset: true,
      },
    });

    blockSeries = chart.addSeries(CandlestickSeries, {
      upColor: theme.success,
      downColor: theme.destructive,
      borderUpColor: theme.success,
      borderDownColor: theme.destructive,
      wickVisible: false,
      borderVisible: true,
    });

    winLineSeries = chart.addSeries(LineSeries, {
      color: theme.success,
      lineWidth: 2,
      crosshairMarkerVisible: false,
      lastValueVisible: false,
      priceLineVisible: false,
    });
    lossLineSeries = chart.addSeries(LineSeries, {
      color: theme.destructive,
      lineWidth: 2,
      crosshairMarkerVisible: false,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    markersPlugin = createSeriesMarkers(blockSeries, []);

    refreshData();
    setupCrosshair();

    // ResizeObserver ajusta width Y height del chart al tamano real del
    // container. Cubre resize de ventana y fullscreen del wrapper padre.
    const ro = new ResizeObserver(() => {
      if (!chart || !container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      if (w > 0 && h > 0) chart.applyOptions({ width: w, height: h });
    });
    ro.observe(container);

    if (onReady) onReady(chart);

    return () => {
      ro.disconnect();
      chart?.remove();
      chart = null;
    };
  });

  function refreshData() {
    if (!chart || !blockSeries || !winLineSeries || !lossLineSeries) return;
    // biome-ignore lint/suspicious/noExplicitAny: lightweight generic types
    blockSeries.setData(getBlockOhlc() as any);
    const { wins, losses } = buildConnectionLines();
    // biome-ignore lint/suspicious/noExplicitAny: WhitespaceData mix con LineData
    winLineSeries.setData(wins as any);
    // biome-ignore lint/suspicious/noExplicitAny: idem
    lossLineSeries.setData(losses as any);
    if (markersPlugin) {
      // biome-ignore lint/suspicious/noExplicitAny: marker generic
      markersPlugin.setMarkers(buildMarkers() as any);
    }
    chart.timeScale().fitContent();
  }

  $effect(() => {
    void blocks;
    void trades;
    untrack(() => refreshData());
  });

  $effect(() => {
    void themeStore.theme;
    untrack(() => {
      if (!chart) return;
      theme = getChartTheme();
      chart.applyOptions({
        layout: { background: { color: theme.background }, textColor: theme.foreground },
        grid: { vertLines: { color: theme.grid }, horzLines: { color: theme.grid } },
        rightPriceScale: { borderColor: theme.border },
        timeScale: { borderColor: theme.border },
      });
      blockSeries?.applyOptions({
        upColor: theme.success,
        downColor: theme.destructive,
        borderUpColor: theme.success,
        borderDownColor: theme.destructive,
      });
      winLineSeries?.applyOptions({ color: theme.success });
      lossLineSeries?.applyOptions({ color: theme.destructive });
      refreshData();
    });
  });
</script>

<div
  class="chart-host relative w-full"
  style="--chart-h: {height}px; height: var(--chart-h);"
>
  <div bind:this={container} class="absolute inset-0"></div>

  {#if tooltipVisible && tooltipContent}
    <div
      class="pointer-events-none absolute z-10 rounded-lg border border-border bg-surface-3/95 px-3 py-2 shadow-luxury backdrop-blur lifted-edge"
      style="left: {tooltipX}px; top: {tooltipY}px;"
    >
      <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
        Block #{tooltipContent.index} · {tooltipContent.time} ET
      </div>
      <div class="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono text-xs tabular-nums">
        <span class="text-muted-foreground">Dir</span>
        <span class={tooltipContent.direction === 'up' ? 'text-success' : 'text-destructive'}>
          {tooltipContent.direction.toUpperCase()}
        </span>
        <span class="text-muted-foreground">Open</span>
        <span>{tooltipContent.open.toFixed(2)}</span>
        <span class="text-muted-foreground">Close</span>
        <span>{tooltipContent.close.toFixed(2)}</span>
        <span class="text-muted-foreground">Box</span>
        <span>{tooltipContent.boxSize.toFixed(2)}</span>
        <span class="text-muted-foreground">ATR</span>
        <span>{tooltipContent.atr.toFixed(2)}</span>
      </div>
    </div>
  {/if}
</div>
